import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/gallery", label: "Gallery" },
    { path: "/service-areas", label: "Service Areas" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/yt963mfm_66C33DB7-9436-4D03-85ED-2BB500C30FDC.jpeg" 
              alt="J Guzman Landscaping Logo"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link py-2 ${isActive(link.path) ? "active" : ""}`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:8159098790"
              className="flex items-center gap-2 text-[#2F5930] font-semibold hover:underline"
              data-testid="header-phone"
            >
              <Phone className="w-5 h-5" />
              <span>(815) 909-8790</span>
            </a>
            <Link to="/contact">
              <Button 
                className="bg-[#2F5930] hover:bg-[#244525] text-white rounded-full px-6"
                data-testid="header-cta-btn"
              >
                Free Estimate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-600 hover:text-[#2F5930]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 animate-fade-in" data-testid="mobile-nav">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`py-2 px-4 rounded-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? "bg-[#F0FDF4] text-[#2F5930]"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="tel:8159098790"
                className="flex items-center gap-2 py-2 px-4 text-[#2F5930] font-semibold"
              >
                <Phone className="w-5 h-5" />
                <span>(815) 909-8790</span>
              </a>
              <a
                href="https://www.facebook.com/Jguzmanlandscaping"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 py-2 px-4 text-[#2F5930] font-semibold"
              >
                <Facebook className="w-5 h-5" />
                <span>Follow us on Facebook</span>
              </a>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-[#2F5930] hover:bg-[#244525] text-white rounded-full">
                  Get Free Estimate
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
